// Phase 78au — Supplier Coord Health page.
// Shows three sections:
//   1. Suggested fixes (suppliers missing coords but have pilot tap history)
//   2. Mismatched (current supplier coords are >500m off from pilot tap median)
//   3. Verified (info only) — suppliers whose coords agree with pilot taps
// Each row has "Apply" to write the suggested coord. Bulk button at the
// top auto-fixes every missing-coord supplier in one click.
//
// Phase 78av — Mismatched rows now have an inline expand-to-preview
// map (red pin = current supplier coord, green pin = pilot-derived
// suggestion, dotted line between) so the admin can visually confirm
// before applying. Lazy-loaded per row to keep Google quota in check.
import React, { useEffect, useState } from 'react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import api from '../../lib/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';

function PreviewMap({ current, suggested }) {
  const { isLoaded } = useGoogleMaps();
  if (!isLoaded) {
    return <div className="h-[220px] bg-[#F3F3F3] flex items-center justify-center text-[10px] uppercase tracking-widest text-[#666]">Loading map…</div>;
  }
  // Center on the midpoint so both pins are visible.
  const mid = {
    lat: (current.lat + suggested.lat) / 2,
    lng: (current.lng + suggested.lng) / 2,
  };
  return (
    <div className="rounded-lg overflow-hidden border border-[#E5E5E5]">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '260px' }}
        center={mid}
        zoom={14}
        options={{ mapId: GOOGLE_MAPS_MAP_ID, disableDefaultUI: true, zoomControl: true, fullscreenControl: true }}
      >
        <Polyline
          path={[current, suggested]}
          options={{ strokeColor: '#7C3AED', strokeOpacity: 0, icons: [{
            icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
            offset: '0', repeat: '10px',
          }] }}
        />
        <AdvancedMarker position={current} title="Current supplier coord">
          <div className="bg-[#FF3333] text-white text-[10px] font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-md border-2 border-white">A</div>
        </AdvancedMarker>
        <AdvancedMarker position={suggested} title="Suggested (pilot taps)">
          <div className="bg-[#00C853] text-white text-[10px] font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-md border-2 border-white">B</div>
        </AdvancedMarker>
      </GoogleMap>
      <div className="flex items-center justify-around bg-[#F8F8F8] text-[9px] uppercase tracking-widest font-bold py-1">
        <span className="text-[#FF3333]">A · Current</span>
        <span className="text-[#7C3AED]">···· drift ····</span>
        <span className="text-[#00C853]">B · Suggested</span>
      </div>
    </div>
  );
}

function CoordRow({ row, kind, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/suppliers/${row.id}/apply-coords`, {
        lat: row.suggested_lat,
        lng: row.suggested_lng,
      });
      onApplied();
    } catch (err) {
      alert(err.response?.data?.detail || 'Apply failed');
    } finally {
      setBusy(false);
    }
  };
  const showPreviewBtn = kind === 'mismatched';
  return (
    <>
      <tr className="border-t border-[#F0F0F0]" data-testid={`coord-row-${row.id}`}>
        <td className="px-3 py-2">
          <div className="font-bold text-sm">{row.name}</div>
          {row.address && <div className="text-[10px] text-[#666] truncate max-w-md">{row.address}</div>}
        </td>
        {kind === 'mismatched' && (
          <td className="px-3 py-2 font-mono text-[11px] text-[#666]">
            {row.current_lat?.toFixed(5)}, {row.current_lng?.toFixed(5)}
          </td>
        )}
        <td className="px-3 py-2 font-mono text-[11px] text-[#00C853]">
          {row.suggested_lat?.toFixed(5)}, {row.suggested_lng?.toFixed(5)}
        </td>
        <td className="px-3 py-2 text-right">
          <span className="bg-[#F3F3F3] rounded-full px-2 py-0.5 text-[10px] font-bold text-[#666]">
            {row.tap_count} taps
          </span>
        </td>
        {kind === 'mismatched' && (
          <td className="px-3 py-2 text-right">
            <span className="font-bold text-[#FF8C00]">{row.delta_m}m off</span>
          </td>
        )}
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {showPreviewBtn && (
            <button
              onClick={() => setPreviewOpen(o => !o)}
              className="text-[10px] font-bold uppercase tracking-widest border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white px-3 py-1 rounded-full mr-1.5 transition-colors"
              data-testid={`coord-row-${row.id}-preview`}
            >🗺️ {previewOpen ? 'Hide' : 'Preview'}</button>
          )}
          <button
            onClick={apply}
            disabled={busy}
            className="text-[10px] font-bold uppercase tracking-widest bg-[#00C853] text-white px-3 py-1 rounded-full hover:opacity-90 disabled:opacity-50"
            data-testid={`coord-row-${row.id}-apply`}
          >{busy ? '…' : 'Apply'}</button>
        </td>
      </tr>
      {showPreviewBtn && previewOpen && (
        <tr data-testid={`coord-row-${row.id}-preview-row`}>
          <td colSpan={6} className="px-3 pb-3 bg-[#FAFAFA]">
            <PreviewMap
              current={{ lat: row.current_lat, lng: row.current_lng }}
              suggested={{ lat: row.suggested_lat, lng: row.suggested_lng }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

export default function SupplierCoordHealth() {
  const [data, setData] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const { data: r } = await api.get('/admin/suppliers/coord-health');
      setData(r);
    } catch (err) {
      alert(err.response?.data?.detail || 'Load failed');
    }
  };
  useEffect(() => { load(); }, []);

  const autoFixAll = async () => {
    if (!window.confirm('Auto-fix coordinates for every supplier in the Missing list?\nThis writes the median of pilot tap GPS as the new supplier coord.')) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post('/admin/suppliers/coord-health/auto-fix-missing');
      alert(`Fixed ${r.fixed} supplier(s).\nSkipped ${r.skipped} (no pilot history yet).`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Auto-fix failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (!data) return <div className="p-4 text-sm text-[#666]">Loading…</div>;
  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="supplier-coord-health">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Supplier Coord Health</h1>
          <p className="text-xs text-[#666] mt-0.5">Pilot tap GPS is used as the source of truth for supplier locations.</p>
        </div>
        <button
          onClick={autoFixAll}
          disabled={bulkBusy || data.missing.length === 0}
          className="bg-[#00C853] text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="auto-fix-all"
        >🧭 Auto-fix {data.missing.length} missing</button>
      </div>

      {/* Stats pills */}
      <div className="flex flex-wrap gap-2">
        <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#0A0A0A] text-white">{t.suppliers} suppliers</span>
        <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#00C853] text-white" data-testid="stat-verified">{t.verified} verified</span>
        <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#FF8C00] text-white" data-testid="stat-mismatched">{t.mismatched} mismatched</span>
        <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A]" data-testid="stat-missing">{t.missing_with_taps} missing (auto-fixable)</span>
        <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest bg-[#CCCCCC] text-[#0A0A0A]" data-testid="stat-no-history">{t.no_history_missing} missing (no pilot history yet)</span>
      </div>

      {/* Missing — auto-fixable */}
      <div className="rounded-2xl border border-[#FFD700]/40 bg-[#FFFCEA] p-4 space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#B27300]">Missing coords · suggest from pilot history ({data.missing.length})</h2>
        {data.missing.length === 0 ? (
          <p className="text-xs text-[#666]">Nothing to fix here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-[#666]">
                <tr className="bg-[#FFF8E1]">
                  <th className="text-left px-3 py-1.5">Supplier</th>
                  <th className="text-left px-3 py-1.5">Suggested</th>
                  <th className="text-right px-3 py-1.5">Confidence</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {data.missing.map(r => <CoordRow key={r.id} row={r} kind="missing" onApplied={load} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mismatched — manual review */}
      <div className="rounded-2xl border border-[#FF8C00]/40 bg-[#FFF4E6] p-4 space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-widest text-[#FF8C00]">Mismatched · supplier coords {">"} 500m from pilot history ({data.mismatched.length})</h2>
        {data.mismatched.length === 0 ? (
          <p className="text-xs text-[#666]">All supplier coords agree with pilot history. Nicely done.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-[#666]">
                <tr className="bg-[#FFE8CC]">
                  <th className="text-left px-3 py-1.5">Supplier</th>
                  <th className="text-left px-3 py-1.5">Current</th>
                  <th className="text-left px-3 py-1.5">Suggested</th>
                  <th className="text-right px-3 py-1.5">Confidence</th>
                  <th className="text-right px-3 py-1.5">Drift</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {data.mismatched.map(r => <CoordRow key={r.id} row={r} kind="mismatched" onApplied={load} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[10px] text-[#666] italic">
        Pilot tap GPS is captured at every "Reached" and "Done" button (Phase 78at). The more pilots use the system, the more accurate the supplier master becomes.
      </p>
    </div>
  );
}
